import { mediaQueriesBP } from "@iewc/Utilities/variables";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { is } from "immer/dist/internal";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const session = state.context.session;
    const isAuthenticated = session && (session.isAuthenticated || session.rememberMe) && !session.isGuest;
    return {
        order: state.pages.orderStatus.order,
        showPoNumber: getSettingsCollection(state).orderSettings.showPoNumber,
        enableVat: getSettingsCollection(state).productSettings.enableVat,
        language: state.context.session.language,
        isAuthenticated,
    };
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps>;

export interface OrderStatusInformationStyles {
    informationWrapper?: InjectableCss;
    wrapper?: InjectableCss;
    titleText?: TypographyPresentationProps;
    orderNumberText?: TypographyPresentationProps;
    statusText?: TypographyPresentationProps;
    orderDateText?: TypographyPresentationProps;
    termsText?: TypographyPresentationProps;
    poNumberText?: TypographyPresentationProps;
    shippingMethodText?: TypographyPresentationProps;
    vatNumberWrapper?: InjectableCss;
    vatNumberText?: TypographyPresentationProps;
}

export const orderStatusInformationStyles: OrderStatusInformationStyles = {
    informationWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: 10px;
            margin-bottom: 20px;

            ${mediaQueriesBP.minMD} {
                margin-bottom: 0;
            }
        `,
    },
    wrapper: {
        css: css`
            margin-right: 25px;
            display: flex;
            gap: 5px;
            font-size: 18px;

            @media screen and (max-width: 500px) {
                margin-right: 0;
            }
        `,
    },
    titleText: {
        weight: 600,
        size: 18,
    },
    vatNumberWrapper: {
        css: css`
            margin-top: 10px;
        `,
    },
};

const styles = orderStatusInformationStyles;

const OrderStatusInformation = ({ order, language,isAuthenticated }: Props) => {
    if (!order) {
        return null;
    }

    const parseOrderType = (orderType = "") => {
        switch (orderType.toUpperCase()) {
            case "CARS":
                return translate("AIR Order");
            case "EDI":
                return translate("EDI Order");
            case "OCR":
                return translate("Sales Rep");
            case "WEB":
                return translate("Web Order");
            case "SA":
                return translate("Scheduled Order");
            default:
                return translate("Sales Rep");
        }
    };

    return (
        <>
            <StyledWrapper {...styles.informationWrapper} data-test-selector="orderStatusInformation">
                <StyledWrapper {...styles.wrapper}>
                    <Typography as="h2" {...styles.titleText} id="orderStatusOrderNumber">
                        {translate("Order #")}:
                    </Typography>
                    <Typography {...styles.orderNumberText} size={18} aria-labelledby="orderStatusOrderNumber">
                        {order.erpOrderNumber || translate("Pending")}
                    </Typography>
                </StyledWrapper>
                {order.webOrderNumber ? (
                    <StyledWrapper {...styles.wrapper}>
                        <Typography as="h2" {...styles.titleText} id="orderStatusOrderNumber">
                            {translate("Web Conf. #")}:
                        </Typography>
                        <Typography {...styles.orderNumberText} size={18} aria-labelledby="orderStatusOrderNumber">
                            {order.webOrderNumber}
                        </Typography>
                    </StyledWrapper>
                ) : null}
                <StyledWrapper {...styles.wrapper}>
                    <Typography as="h2" {...styles.titleText} id="orderStatusOrderNumber">
                        {translate("Order type")}:
                    </Typography>
                    <Typography {...styles.orderNumberText} size={18} aria-labelledby="orderStatusOrderNumber">
                        {parseOrderType(order.properties?.orderChannel)}
                    </Typography>
                </StyledWrapper>
                <StyledWrapper {...styles.wrapper}>
                    <Typography as="h2" {...styles.titleText} id="orderStatusStatus">
                        {translate("Order Date")}:
                    </Typography>
                    <Typography {...styles.statusText} aria-labelledby="orderStatusStatus">
                        {order.orderDate ? getLocalizedDateTime({ dateTime: new Date(order.orderDate), language }) : ""}
                    </Typography>
                </StyledWrapper>
                <StyledWrapper {...styles.wrapper}>
                    <Typography as="h2" {...styles.titleText} id="orderStatusStatus">
                        {translate("Order Status")}:
                    </Typography>
                    <Typography {...styles.statusText} aria-labelledby="orderStatusStatus">
                        {order.status}
                    </Typography>
                </StyledWrapper>
                <StyledWrapper {...styles.wrapper}>
                    <Typography as="h2" {...styles.titleText} id="orderStatusOrderDate">
                        {translate("Customer P.O")}:
                    </Typography>
                    <Typography {...styles.statusText} aria-labelledby="orderStatusStatus">
                        {order.customerPO}
                    </Typography>
                </StyledWrapper>
                {!isAuthenticated && (
                     <StyledWrapper {...styles.wrapper}>
                     <Typography as="h2" {...styles.titleText} id="orderStatusOrderDate">
                         {translate("Customer SAP Account #")}:
                     </Typography>
                     <Typography {...styles.statusText} aria-labelledby="orderStatusStatus">
                         {order.customerNumber.replace(/^0+/, "")}
                     </Typography>
                 </StyledWrapper>
                )}
            </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(OrderStatusInformation),
    definition: {
        group: "Order Status",
        displayName: "Information",
        allowedContexts: ["OrderStatusPage"],
    },
};

export default widgetModule;
